import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import KODIAK_REWARDS_ABI from 'abis/KodiakRewards.json'
import { ButtonPrimary } from 'components/Button'
import { LightCard } from 'components/Card'
import CircularProgress from 'components/CircularProgress'
import Column, { AutoColumn } from 'components/Column'
import CurrencyLogo from 'components/CurrencyLogo'
import FiatValue from 'components/FiatValue'
import { LoadingRows } from 'components/Loader/styled'
import PoweredByKodiak from 'components/PoweredByKodiak'
import Row, { AutoRow, RowBetween } from 'components/Row'
import TimeBetweenText from 'components/TimeBetweenText'
import { KODIAK_REWARDS_ADDRESSES } from 'constants/addresses'
import { useConnection } from 'hooks/useConnection'
import { useDividends } from 'hooks/useStaking'
import useTheme from 'hooks/useTheme'
import { useGetTokenPrices } from 'hooks/useTokenPrice'
import { useWriteContract } from 'hooks/useTransaction'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Text } from 'rebass'
import { useTransactionAdder } from 'state/transactions/hooks'
import { TransactionType } from 'state/transactions/types'
import { calculateGasMargin } from 'utils/calculateGasMargin'
import { formatCurrencyAmount } from 'utils/formatCurrencyAmount'
import { formatAmount } from 'utils/formatDollarAmt'
import type { Abi } from 'viem'

import { HoverText, ThemedText } from '../../theme'
import ManagerAllocationButtons from './ManageAllocationButtons'
import * as S from './styleds'
import UserAllocationInfo from './UserAllocationInfo'
import UserClaimDividends from './UserClaimDividends'

export default function DividendsPage() {
  const theme = useTheme()
  const { rewardsInfo, nextCycleStartTime, pendingRewardsAmount, cycleDurationSeconds } = useDividends()
  const { chainId, account, wagmiPublicClient } = useConnection()
  const rewardsAddress = KODIAK_REWARDS_ADDRESSES[chainId]
  const [harvesting, setHarvesting] = useState(false)
  const { writeContractAsync } = useWriteContract()
  const addTransaction = useTransactionAdder()
  const isActiveHarvest = pendingRewardsAmount.some((amount) => amount !== undefined && amount.greaterThan('0'))

  const rewardTokens = useMemo(() => {
    return pendingRewardsAmount
      .map((amount) => amount?.currency.address)
      .filter((address): address is string => !!address)
  }, [pendingRewardsAmount])

  const { prices } = useGetTokenPrices(rewardTokens)

  const totalRewardsUSD = useMemo(() => {
    if (!prices || !pendingRewardsAmount) return 0

    const validAmounts = pendingRewardsAmount.filter(
      (amount): amount is CurrencyAmount<Token> => !!amount && !!amount.currency.address
    )

    if (validAmounts.length !== prices.length) return 0

    return validAmounts.reduce((acc, amount, index) => {
      const price = prices[index]
      if (typeof price !== 'number') return acc

      const value = parseFloat(amount.toExact()) * price
      return acc + value
    }, 0)
  }, [prices, pendingRewardsAmount])

  const endCyclePercentage =
    nextCycleStartTime && cycleDurationSeconds
      ? Math.min(
          100,
          Math.max(0, ((Date.now() / 1000 - (nextCycleStartTime - cycleDurationSeconds)) / cycleDurationSeconds) * 100)
        )
      : 0

  const harvestRewards = async () => {
    if (!rewardsAddress || !wagmiPublicClient || !account) return
    setHarvesting(true)

    try {
      const tx = {
        address: rewardsAddress,
        abi: KODIAK_REWARDS_ABI as Abi,
        functionName: 'harvestAllRewards',
        account,
      }
      const gas = await wagmiPublicClient.estimateContractGas(tx)

      const txHash = await writeContractAsync({ ...tx, gas: calculateGasMargin(gas) })
      addTransaction({ hash: txHash }, { type: TransactionType.DIVIDENDS_HARVEST })
    } catch (error) {
      console.error('Error harvesting rewards:', error)
    } finally {
      setHarvesting(false)
    }
  }

  return (
    <>
      <S.Wrapper>
        <>
          <Link
            data-cy="visit-staking"
            style={{ textDecoration: 'none', width: 'fit-content', marginBottom: '0.5rem' }}
            to="/staking"
          >
            <HoverText>
              <>← Back to KDK Overview</>
            </HoverText>
          </Link>
          <S.StakingCardFlex>
            <S.AdaptiveFlexRowBetween>
              <S.FlexCol>
                <ThemedText.DeprecatedBody fontSize="18px" fontWeight="700" lineHeight="28px" color={theme.textPrimary}>
                  Protocol Rewards
                </ThemedText.DeprecatedBody>

                <ThemedText.DeprecatedBody
                  fontSize="14px"
                  fontWeight="400"
                  lineHeight="20px"
                  color={theme.textSecondary}
                >
                  Stake xKDK to earn protocol rewards.
                </ThemedText.DeprecatedBody>
              </S.FlexCol>
            </S.AdaptiveFlexRowBetween>
          </S.StakingCardFlex>

          <S.CardRowFlex>
            <S.StakingCardWrapperFlex>
              <S.StakingCardFlex>
                <AutoColumn>
                  <Text>Current epoch</Text>
                </AutoColumn>

                {rewardsInfo.length === 0 && (
                  <LightCard>
                    <LoadingRows>
                      <div></div>
                    </LoadingRows>
                  </LightCard>
                )}
                {rewardsInfo ? (
                  <LightCard>
                    <AutoColumn gap="16px">
                      {rewardsInfo.map((info, index) =>
                        info ? (
                          <AutoRow gap="8px" key={index}>
                            <div>
                              <CurrencyLogo size="32px" currency={info?.currentCycleDistributedAmount?.currency} />
                            </div>
                            <Column>
                              <Text fontSize={14} color={theme.textSecondary}>
                                {info?.currentCycleDistributedAmount?.currency?.symbol}
                              </Text>
                              <Row>
                                <Text fontSize={14} color={theme.textPrimary}>
                                  {formatCurrencyAmount(info?.currentDistributionAmount, 4)}
                                </Text>
                                <Text fontSize={14} color={theme.textSecondary} marginLeft="4px">
                                  (<FiatValue currencyAmount={info?.currentDistributionAmount} />)
                                </Text>
                              </Row>
                            </Column>
                          </AutoRow>
                        ) : (
                          <LoadingRows key={index}>
                            <div></div>
                          </LoadingRows>
                        )
                      )}
                    </AutoColumn>
                  </LightCard>
                ) : (
                  <></>
                )}

                <AutoColumn>
                  <Text>Next epoch</Text>
                </AutoColumn>
                <AutoColumn gap="4px">
                  <LightCard>
                    {!nextCycleStartTime || isNaN(nextCycleStartTime) ? (
                      <LoadingRows>
                        <div></div>
                      </LoadingRows>
                    ) : (
                      <RowBetween>
                        <Text color={theme.textSecondary} fontSize={14}>
                          Remaining time
                        </Text>
                        <Row width="auto" gap="4px">
                          <CircularProgress size={16} percentage={endCyclePercentage} />
                          <Text color={theme.textPrimary} fontSize={14}>
                            <TimeBetweenText date={new Date(nextCycleStartTime * 1000)} />
                          </Text>
                        </Row>
                      </RowBetween>
                    )}
                  </LightCard>
                </AutoColumn>
              </S.StakingCardFlex>
            </S.StakingCardWrapperFlex>

            <S.StakingCardWrapperFlex>
              <S.StakingCardFlex>
                <RowBetween>
                  <Text>Your staking</Text>
                </RowBetween>
                <UserAllocationInfo />
                {pendingRewardsAmount.some((item) => item !== undefined) ? (
                  <>
                    <RowBetween>
                      <Text>Your Rewards</Text>
                      <AutoRow width="fit-content">
                        <ButtonPrimary disabled={!isActiveHarvest || harvesting} onClick={harvestRewards}>
                          Claim all ≈ ${formatAmount(totalRewardsUSD, 2)}
                        </ButtonPrimary>
                      </AutoRow>
                    </RowBetween>

                    <UserClaimDividends />
                    <ManagerAllocationButtons />
                  </>
                ) : (
                  account && <ManagerAllocationButtons />
                )}
              </S.StakingCardFlex>
            </S.StakingCardWrapperFlex>
          </S.CardRowFlex>
        </>
      </S.Wrapper>
      <PoweredByKodiak />
    </>
  )
}
